import React, { useState, useCallback, useRef, useEffect } from 'react';
import { message, Spin } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConfigAndSendQuestion from './components/configAndSendQuestion';
import RunningProcess from './components/runningProcess';
import ReadonlyResultView from './components/readonlyResult';
import PauseKnowledgeBaseModal from './components/runningProcess/PauseKnowledgeBaseModal';
import type { StepStatus } from './components/runningProcess/StepTimeline';
import {
  createScenarioTaskOrchestratorStream,
  getKnowledgeBaseList,
  getOntologyTree,
  getOrganizationList,
  getScenarioTaskProcessLogsStream,
  getScenarioTaskRecordDetail,
  getScenarioTaskRecordDetailByRequestId,
  resumeScenarioTaskProcess,
  type KnowledgeBaseItem,
  type OntologyNode,
  type OrganizationItem,
  type ScenarioTaskRecordItem,
} from '../../api';
import { StreamQueue } from '../../utils/streamQueue';
import { useConfig } from '../../hooks/useConfig';
import { useAppStore } from '../../store';
import { usePageEventQueue } from './hooks/usePageEventQueue';

/** 运行场景任务的流程阶段 */
type TaskPhase = 'config' | 'running' | 'result' | 'processView';

/** 第三步完成提示停留时长：当前事件先消费，后续第四步事件再由页面队列延后放行。 */
const STEP4_POST_DELAY_MS = 1200;
/** 第四步完成并执行最终视口动画后，再等待几秒进入结果页。 */
const RESULT_PAGE_DELAY_MS = 3000;

/** 对话消息 */
export type AnswerDisplayMode = 'text' | 'json';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  time?: string;
  /** AI 回复展示模式：普通文本 or 结构化 JSON。 */
  displayMode?: AnswerDisplayMode;
  /** 结构化 JSON 数据，供结果区组件格式化展示。 */
  structuredContent?: unknown;
}

/** 运行配置信息，用于在运行过程页面左侧展示 + 接口请求 */
export interface RunConfig {
  recordId?: number;
  requestId?: string;
  orgId?: string;
  orgName?: string;
  scenarioId?: number;
  scenarioName?: string;
  objectOntologyId?: number;
  objectOntologyName?: string;
  knowledgeBaseId?: string;
  knowledgeBaseName?: string;
  createdAt?: string;
  processJson: string;
  variables: {
    key: string;
    /** 变量类型：number 走数值输入，string 走文本输入 */
    type: 'number' | 'string';
    value: string | number | null;
  }[];
}

/** 日志条目 */
export interface LogEntry {
  type: string;
  category: string;
  name: string;
  content: string;
  /** 运行阶段后端可能携带的真实过程节点名 */
  processName?: string;
  history?: string;
  timestamp?: string;
}

interface DatasetPauseState {
  content: string;
  processName: string;
}

type InternalPageStepEventName =
  | 'auto-complete-recruiting'
  | 'auto-complete-matching';

interface InternalPageEvent {
  type: 'internal';
  category: 'page-step';
  name: string;
  content: InternalPageStepEventName;
  processName?: string;
  timestamp?: string;
}

type PageQueueEntry = LogEntry | InternalPageEvent;

interface StepFlowState {
  hasRecruitingStarted: boolean;
  hasRecruitingCompleted: boolean;
  hasMatchingCompleted: boolean;
  hasAutoSkipQueued: boolean;
}

/** 第 2/3 步被后端跳过时，前端仍保留一个短暂的依次打勾动画。 */
const AUTO_SKIP_STEP_HOLD_MS = 500;

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString('zh-CN', { hour12: false });
}

function createInitialStepFlowState(): StepFlowState {
  return {
    hasRecruitingStarted: false,
    hasRecruitingCompleted: false,
    hasMatchingCompleted: false,
    hasAutoSkipQueued: false,
  };
}

function isInternalPageEvent(
  event: PageQueueEntry
): event is InternalPageEvent {
  return event.type === 'internal' && event.category === 'page-step';
}

function summarizeStepDebugContent(content?: string): string {
  if (!content) return '';
  return content.length > 120 ? `${content.slice(0, 120)}…` : content;
}

function formatStepDebugEvent(event?: PageQueueEntry | null) {
  if (!event) return null;
  if (isInternalPageEvent(event)) {
    return {
      type: event.type,
      category: event.category,
      content: event.content,
    };
  }

  return {
    type: event.type,
    category: event.category,
    name: event.name,
    content: summarizeStepDebugContent(event.content),
    history: event.history,
    processName: event.processName,
  };
}

/** 第四步显式开始日志：后端新增，用于明确标识“运行场景任务”起点。 */
function isStepFourStartLog(event: LogEntry): boolean {
  return (
    event.type === 'log' &&
    event.category === 'process' &&
    event.content === '开始执行过程'
  );
}

/** 第四步显式结束日志：后端新增，用于明确标识“运行场景任务”终点。 */
function isStepFourCompleteLog(event: LogEntry): boolean {
  return (
    event.type === 'log' &&
    event.category === 'process' &&
    event.content === '完成执行过程'
  );
}

function isImplicitStepFourRuntimeEvent(event: LogEntry): boolean {
  if (event.type === 'think') {
    return !!(event.processName || event.name);
  }

  if (event.type !== 'log' || event.category !== 'process') {
    return false;
  }

  const content = event.content || '';
  if (content === '开始执行过程' || content === '完成执行过程') {
    return false;
  }

  return (
    !!(event.processName || event.name) &&
    ((content.startsWith('开始') && content.includes('执行')) ||
      (content.startsWith('完成') && content.includes('执行')))
  );
}

function getStepStatusesScore(statuses: StepStatus[]): number {
  if (statuses[3] === 'completed') return 45;
  if (statuses[3] === 'active') return 40;
  if (statuses[2] === 'completed') return 35;
  if (statuses[2] === 'active') return 30;
  if (statuses[1] === 'completed') return 25;
  if (statuses[1] === 'active') return 20;
  if (statuses[0] === 'completed') return 15;
  if (statuses[0] === 'active') return 10;
  return 0;
}

/**
 * 将不完整的 JSONL 数据块拆分并解析为 JSON 对象数组
 * 与 organization/create.tsx 的 parseStreamChunk 逻辑一致
 */
function parseStreamChunk(
  dataStr: string,
  cache: { current: string }
): LogEntry[] {
  cache.current += dataStr;

  const tryParse = (str: string): LogEntry | null => {
    try {
      return JSON.parse(str);
    } catch {
      return null;
    }
  };

  const results: LogEntry[] = [];

  // 按换行拆分，逐行尝试解析（JSONL 格式每行一个 JSON 对象）
  const lines = cache.current.split('\n');
  // 最后一段可能不完整，保留在缓存中
  cache.current = lines.pop() || '';

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    // 处理同一行多个 JSON 对象粘连的情况，如 }{
    if (trimmed.includes('}{')) {
      const parts = trimmed.split('}{');
      for (let i = 0; i < parts.length; i++) {
        let part = parts[i];
        if (i === 0) {
          part = part + '}';
        } else if (i === parts.length - 1) {
          part = '{' + part;
        } else {
          part = '{' + part + '}';
        }
        const parsed = tryParse(part);
        if (parsed) results.push(parsed);
      }
    } else {
      const parsed = tryParse(trimmed);
      if (parsed) results.push(parsed);
    }
  }

  // 尝试解析缓存中残留的内容（可能是完整的最后一行无换行结尾）
  if (cache.current.trim()) {
    const parsed = tryParse(cache.current.trim());
    if (parsed) {
      results.push(parsed);
      cache.current = '';
    }
  }

  return results;
}

/**
 * 用户问题若本身是 JSON（常见于多行问答列表），
 * 则在提交前转成“带转义符的字符串内容”，与后端 Postman 模拟格式保持一致。
 *
 * 例如：
 * - 输入：多行 JSON 数组文本
 * - 输出：包含 `\\n` / `\\\"` 的字符串内容
 *
 * 普通自然语言问题保持原样，不做额外处理。
 */
function normalizeQuestionParameter(question: string): string {
  const trimmed = question.trim();
  if (!trimmed) return question;

  try {
    const parsed = JSON.parse(trimmed);
    const isStructuredJson =
      Array.isArray(parsed) || (parsed !== null && typeof parsed === 'object');
    if (!isStructuredJson) return question;

    const formattedJson = JSON.stringify(parsed, null, 2);
    return JSON.stringify(formattedJson).slice(1, -1);
  } catch {
    return question;
  }
}

function isPlainArticleAnswer(
  value: unknown
): value is Record<string, string | number | boolean | null> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value).every(item => {
    const type = typeof item;
    return (
      item === null ||
      type === 'string' ||
      type === 'number' ||
      type === 'boolean'
    );
  });
}

function createAssistantAnswerPayload(
  raw: string
): Pick<ChatMessage, 'content' | 'displayMode' | 'structuredContent'> {
  if (!raw) {
    return {
      content: '暂无处理结果',
      displayMode: 'text',
    };
  }

  try {
    const parsed = JSON.parse(raw);
    if (isPlainArticleAnswer(parsed)) {
      return {
        content: Object.entries(parsed)
          .map(([k, v]) => `${k}：${v}`)
          .join('\n\n'),
        displayMode: 'text',
      };
    }

    if (parsed !== null && typeof parsed === 'object') {
      return {
        content: raw,
        displayMode: 'json',
        structuredContent: parsed,
      };
    }
  } catch {
    // 非 JSON 文本按原样展示
  }

  return {
    content: raw,
    displayMode: 'text',
  };
}

function createErrorAssistantMessage(
  errorMessage: string,
  time?: string
): ChatMessage {
  return {
    role: 'assistant',
    content: `执行失败：${errorMessage}`,
    displayMode: 'text',
    time,
  };
}

interface ScenarioTaskReferenceData {
  organizations: OrganizationItem[];
  ontologyTree: OntologyNode[];
  knowledgeBases: KnowledgeBaseItem[];
}

const RESERVED_PARAMETER_KEYS = new Set([
  '组织ID',
  '组织名称',
  '本体ID',
  'namespace',
  'processName',
  '场景本体ID',
  '场景本体名称',
  '对象本体ID',
  '对象本体名称',
  '知识库ID',
  '知识库名称',
  '用户问题',
]);

function toNumberValue(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }

  return undefined;
}

function toStringValue(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim() !== '') return value;
  return undefined;
}

function findOntologyNameById(
  nodes: OntologyNode[],
  ontologyId?: number
): string | undefined {
  if (ontologyId === undefined) return undefined;

  for (const node of nodes) {
    if (node.type === 'ONTOLOGY' && node.ontologyId === ontologyId) {
      return node.name;
    }

    if (node.children?.length) {
      const found = findOntologyNameById(node.children, ontologyId);
      if (found) return found;
    }
  }

  return undefined;
}

function extractProcessDisplayName(
  processJson?: string | null
): string | undefined {
  if (!processJson) return undefined;

  try {
    const parsed = JSON.parse(processJson) as {
      process?: { name?: string };
      name?: string;
    };
    return parsed.process?.name || parsed.name;
  } catch {
    return undefined;
  }
}

function normalizeQuestionText(value: unknown): string {
  if (typeof value !== 'string') return '';

  return value.replace(/\\n/g, '\n').replace(/\\"/g, '"');
}

function parseScenarioTaskParameters(
  value: unknown
): Record<string, unknown> | null {
  if (!value) return null;

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }

  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function extractOpmParameters(
  processJson?: string | null
): Record<string, unknown> | null {
  if (!processJson) return null;

  try {
    const parsed = JSON.parse(processJson) as {
      parameters?: unknown;
    };
    return parseScenarioTaskParameters(parsed.parameters);
  } catch {
    return null;
  }
}

function getScenarioTaskParameters(
  record: ScenarioTaskRecordItem
): Record<string, unknown> {
  return (
    parseScenarioTaskParameters(record.parameters) ||
    extractOpmParameters(record.opmJson) ||
    {}
  );
}

function buildRunConfigFromRecord(
  record: ScenarioTaskRecordItem,
  referenceData: ScenarioTaskReferenceData
): RunConfig {
  const parameters = getScenarioTaskParameters(record);
  const organizationId =
    toStringValue(parameters['组织ID']) || record.organizationId || undefined;
  const scenarioId = toNumberValue(parameters['场景本体ID']);
  const objectOntologyId =
    toNumberValue(parameters['对象本体ID']) ??
    toNumberValue(parameters['本体ID']) ??
    record.ontologyId ??
    undefined;
  const knowledgeBaseId = toStringValue(parameters['知识库ID']);
  const variables = Object.entries(parameters)
    .filter(([key]) => !RESERVED_PARAMETER_KEYS.has(key))
    .map(([key, value]) => ({
      key,
      type:
        typeof value === 'number' ? ('number' as const) : ('string' as const),
      value:
        typeof value === 'number'
          ? value
          : typeof value === 'string'
            ? value
            : value == null
              ? ''
              : JSON.stringify(value),
    }));

  return {
    recordId: record.id,
    requestId: record.requestId || undefined,
    orgId: organizationId,
    orgName:
      toStringValue(parameters['组织名称']) ||
      referenceData.organizations.find(item => item.id === organizationId)
        ?.name ||
      organizationId ||
      undefined,
    scenarioId,
    scenarioName:
      toStringValue(parameters['场景本体名称']) ||
      findOntologyNameById(referenceData.ontologyTree, scenarioId) ||
      extractProcessDisplayName(record.opmJson),
    objectOntologyId,
    objectOntologyName:
      toStringValue(parameters['对象本体名称']) ||
      record.ontologyName ||
      findOntologyNameById(referenceData.ontologyTree, objectOntologyId),
    knowledgeBaseId,
    knowledgeBaseName:
      toStringValue(parameters['知识库名称']) ||
      referenceData.knowledgeBases.find(item => item.id === knowledgeBaseId)
        ?.name ||
      knowledgeBaseId,
    createdAt: record.createdAt ? formatDateTime(record.createdAt) : undefined,
    processJson: record.opmJson || '',
    variables,
  };
}

function buildMessagesFromRecord(
  record: ScenarioTaskRecordItem
): ChatMessage[] {
  const parameters = getScenarioTaskParameters(record);
  const question = normalizeQuestionText(parameters['用户问题']);
  const messages: ChatMessage[] = [];

  if (question) {
    messages.push({
      role: 'user',
      content: question,
      time: record.createdAt ? formatDateTime(record.createdAt) : undefined,
    });
  }

  if (record.resultText) {
    const answerPayload = createAssistantAnswerPayload(record.resultText);
    messages.push({
      role: 'assistant',
      content: answerPayload.content,
      displayMode: answerPayload.displayMode,
      structuredContent: answerPayload.structuredContent,
      time: record.updatedAt ? formatDateTime(record.updatedAt) : undefined,
    });
  }

  if (record.errorMessage) {
    messages.push(
      createErrorAssistantMessage(
        record.errorMessage,
        record.updatedAt ? formatDateTime(record.updatedAt) : undefined
      )
    );
  }

  return messages;
}

const ScenarioTaskPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const detailId = searchParams.get('id');
  const detailRequestId = searchParams.get('requestId');
  // 当前流程阶段
  const [phase, setPhase] = useState<TaskPhase>('config');
  const [pageLoading, setPageLoading] = useState(false);
  // 对话消息列表
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // 运行配置（点击开始执行时保存）
  const [runConfig, setRunConfig] = useState<RunConfig | null>(null);
  // 用户问题
  const [userQuestion, setUserQuestion] = useState('');
  const [referenceData, setReferenceData] = useState<ScenarioTaskReferenceData>(
    {
      organizations: [],
      ontologyTree: [],
      knowledgeBases: [],
    }
  );
  // 四个步骤的状态
  const [stepStatuses, setStepStatuses] = useState<StepStatus[]>([
    'pending',
    'pending',
    'pending',
    'pending',
  ]);
  // 运行日志
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  // answer 内容缓存
  const answerContentRef = useRef('');
  // 请求取消控制器
  const abortControllerRef = useRef<AbortController | null>(null);
  // 完成后延迟跳转的定时器
  const completeTimerRef = useRef<number | null>(null);
  /** 第三步完成后，等待真正的第四步日志到来时再激活步骤条。 */
  const pendingStepFourActivationRef = useRef(false);
  /** 当前这轮执行生成的 AI 结果消息索引，用于 answer 晚到时就地更新。 */
  const currentAnswerMessageIndexRef = useRef<number | null>(null);
  const readonlyReplayRef = useRef(false);
  // antd message
  const [messageApi, contextHolder] = message.useMessage();
  // 是否正在运行
  const [isRunning, setIsRunning] = useState(false);
  const [isHistoryReplaying, setIsHistoryReplaying] = useState(false);
  /** 最近一次执行过程是否可回看；新一轮执行开始时重置，完成后打开入口。 */
  const [canViewLastProcess, setCanViewLastProcess] = useState(false);
  const [datasetPause, setDatasetPause] = useState<DatasetPauseState | null>(
    null
  );
  const [resumeSubmitting, setResumeSubmitting] = useState(false);
  /** 完成事件到达后，等待过程图最终 fit 动画完成，再开始结果页倒计时。 */
  const waitingDoneViewportRef = useRef(false);
  /** 当前任务 requestId 同步保存在 ref，避免 pause 确认时读到旧闭包。 */
  const currentRequestIdRef = useRef<string | undefined>(undefined);
  const setScenarioTaskRequestId = useAppStore(
    state => state.setScenarioTaskRequestId
  );
  // 流消息队列引擎（演示模式下控制日志输出速度）
  const streamQueueRef = useRef<StreamQueue<LogEntry> | null>(null);
  /** 四步流转的关键里程碑，用于判断是否需要自动补第 2/3 步打勾。 */
  const stepFlowRef = useRef<StepFlowState>(createInitialStepFlowState());
  const logStepDebug = useCallback(
    (
      stage: string,
      event?: PageQueueEntry | null,
      extra?: Record<string, unknown>
    ) => {
      console.info('[ScenarioTask][StepDebug]', {
        stage,
        event: formatStepDebugEvent(event),
        pendingStepFourActivation: pendingStepFourActivationRef.current,
        stepFlow: { ...stepFlowRef.current },
        ...extra,
      });
    },
    []
  );
  const updateStepStatuses = useCallback(
    (
      next: StepStatus[],
      stage: string,
      event?: PageQueueEntry | null,
      extra?: Record<string, unknown>,
      force = false
    ) => {
      setStepStatuses(prev => {
        const prevScore = getStepStatusesScore(prev);
        const nextScore = getStepStatusesScore(next);
        const skipped = !force && nextScore < prevScore;
        console.info('[ScenarioTask][StepDebug][setStepStatuses]', {
          stage,
          prev,
          next,
          prevScore,
          nextScore,
          skipped,
          force,
          event: formatStepDebugEvent(event),
          pendingStepFourActivation: pendingStepFourActivationRef.current,
          stepFlow: { ...stepFlowRef.current },
          ...extra,
        });
        return skipped ? prev : next;
      });
    },
    []
  );
  /** 右侧日志缓冲：避免每条日志都触发整页 React 更新。 */
  const pendingLogsRef = useRef<LogEntry[]>([]);
  /** 日志批量提交定时器。 */
  const flushLogsTimerRef = useRef<number | null>(null);
  /** 页面层队列控制器：统一管理暂停 / 继续 / flush / reset。 */
  const {
    isPaused: isPageQueuePaused,
    createQueue: createPageQueue,
    enqueue: enqueueToPageQueue,
    flush: flushPageQueue,
    reset: resetPageQueue,
    togglePause: togglePageQueue,
    pause: pausePageQueue,
    resume: resumePageQueue,
  } = usePageEventQueue<PageQueueEntry>();
  const { globalConfig } = useConfig();
  const demoCfg = globalConfig as {
    demoMode?: boolean;
    demoPageConsumeInterval?: number;
    demoPageStepHoldMs?: number;
    enableScenarioTaskPauseControl?: boolean;
  };
  const enablePauseControl = demoCfg.enableScenarioTaskPauseControl !== false;
  const pageConsumeIntervalMs =
    demoCfg.demoMode === true ? (demoCfg.demoPageConsumeInterval ?? 200) : 0;
  const pageStepHoldMs =
    demoCfg.demoMode === true ? (demoCfg.demoPageStepHoldMs ?? 1500) : 0;
  const autoSkipStepHoldMs =
    pageStepHoldMs > 0 ? pageStepHoldMs : AUTO_SKIP_STEP_HOLD_MS;
  const resetToList = useCallback(() => {
    navigate('/scenario-task-list');
  }, [navigate]);
  const loadReferenceData = useCallback(async () => {
    const [organizations, ontologyTree, knowledgeBases] = await Promise.all([
      getOrganizationList({ status: 'enabled' }),
      getOntologyTree(),
      getKnowledgeBaseList(),
    ]);

    const nextReferenceData = {
      organizations,
      ontologyTree,
      knowledgeBases,
    };

    setReferenceData(nextReferenceData);
    return nextReferenceData;
  }, []);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      streamQueueRef.current?.destroy();
      if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
      if (flushLogsTimerRef.current) clearTimeout(flushLogsTimerRef.current);
    };
  }, []);

  /** 第四步动画完成后的统一收尾：等待几秒再切到结果页。 */
  const finishRunAfterViewportSettled = useCallback(() => {
    if (readonlyReplayRef.current) {
      waitingDoneViewportRef.current = false;
      return;
    }
    waitingDoneViewportRef.current = false;
    if (completeTimerRef.current !== null) {
      window.clearTimeout(completeTimerRef.current);
    }
    completeTimerRef.current = window.setTimeout(() => {
      // 解析 answer 内容并添加 AI 消息
      const raw = answerContentRef.current;
      const answerPayload = createAssistantAnswerPayload(raw);
      setMessages(prev => {
        const nextIndex = prev.length;
        currentAnswerMessageIndexRef.current = nextIndex;
        return [
          ...prev,
          {
            role: 'assistant',
            content: answerPayload.content,
            displayMode: answerPayload.displayMode,
            structuredContent: answerPayload.structuredContent,
            time: new Date().toLocaleString('zh-CN'),
          },
        ];
      });
      setCanViewLastProcess(true);
      setPhase('result');
      setIsRunning(false);
    }, RESULT_PAGE_DELAY_MS);
  }, []);

  /**
   * 页面层步骤停留预览：
   * - 先把当前步骤标记为 completed
   * - 暂不激活下一步，留出页面展示停留时间
   */
  const applyStepTransitionPreview = useCallback(
    (event: PageQueueEntry) => {
      if (isInternalPageEvent(event)) {
        if (event.content === 'auto-complete-recruiting') {
          updateStepStatuses(
            ['completed', 'completed', 'pending', 'pending'],
            'applyStepTransitionPreview:auto-complete-recruiting',
            event
          );
          return true;
        }
        if (event.content === 'auto-complete-matching') {
          updateStepStatuses(
            ['completed', 'completed', 'completed', 'pending'],
            'applyStepTransitionPreview:auto-complete-matching',
            event
          );
          return true;
        }
        return false;
      }

      const { type, content } = event;
      if (type !== 'log' || pageStepHoldMs <= 0) return false;

      if (content === '开始招募') {
        updateStepStatuses(
          ['completed', 'pending', 'pending', 'pending'],
          'applyStepTransitionPreview:开始招募',
          event
        );
        return true;
      }
      if (content === '完成招募') {
        updateStepStatuses(
          ['completed', 'completed', 'pending', 'pending'],
          'applyStepTransitionPreview:完成招募',
          event
        );
        return true;
      }
      if (content === '完成全部组队') {
        updateStepStatuses(
          ['completed', 'completed', 'completed', 'pending'],
          'applyStepTransitionPreview:完成全部组队',
          event
        );
        return true;
      }
      return false;
    },
    [pageStepHoldMs, updateStepStatuses]
  );

  /** 批量提交日志，降低右侧日志面板对整页的重渲染频率。 */
  const flushPendingLogs = useCallback(() => {
    flushLogsTimerRef.current = null;
    if (pendingLogsRef.current.length === 0) return;
    const batch = pendingLogsRef.current;
    pendingLogsRef.current = [];
    setLogEntries(prev => [...prev, ...batch]);
  }, []);

  /** 处理单条流式事件：判断步骤状态 + 收集日志 + 提取 answer */
  const processStreamEvent = useCallback(
    (event: PageQueueEntry) => {
      logStepDebug('processStreamEvent:received', event);

      if (isInternalPageEvent(event)) {
        if (event.content === 'auto-complete-recruiting') {
          updateStepStatuses(
            ['completed', 'completed', 'pending', 'pending'],
            'processStreamEvent:auto-complete-recruiting',
            event
          );
        } else if (event.content === 'auto-complete-matching') {
          updateStepStatuses(
            ['completed', 'completed', 'completed', 'pending'],
            'processStreamEvent:auto-complete-matching',
            event
          );
        }
        return;
      }

      const { content, type } = event;
      const isExplicitStepFourStart = isStepFourStartLog(event);
      const isImplicitStepFourStart = isImplicitStepFourRuntimeEvent(event);

      if (
        pendingStepFourActivationRef.current &&
        (isExplicitStepFourStart ||
          (type === 'log' &&
            content !== '完成全部组队' &&
            content !== '开始执行过程') ||
          type === 'think')
      ) {
        pendingStepFourActivationRef.current = false;
        updateStepStatuses(
          ['completed', 'completed', 'completed', 'active'],
          'processStreamEvent:pending-step4-activation',
          event,
          { isExplicitStepFourStart, isImplicitStepFourStart }
        );
      }

      // 步骤状态判断
      if (content === '开始构造结构') {
        updateStepStatuses(
          ['active', 'pending', 'pending', 'pending'],
          'processStreamEvent:开始构造结构',
          event
        );
      } else if (content === '开始招募') {
        stepFlowRef.current.hasRecruitingStarted = true;
        updateStepStatuses(
          ['completed', 'active', 'pending', 'pending'],
          'processStreamEvent:开始招募',
          event
        );
      } else if (content === '完成招募') {
        stepFlowRef.current.hasRecruitingCompleted = true;
        updateStepStatuses(
          ['completed', 'completed', 'active', 'pending'],
          'processStreamEvent:完成招募',
          event
        );
      } else if (content === '完成全部组队') {
        stepFlowRef.current.hasMatchingCompleted = true;
        // 当前事件先落地，第四步真正开始时再由后续运行日志激活步骤条。
        pendingStepFourActivationRef.current = true;
        updateStepStatuses(
          ['completed', 'completed', 'completed', 'pending'],
          'processStreamEvent:完成全部组队',
          event
        );
      } else if (isExplicitStepFourStart) {
        pendingStepFourActivationRef.current = false;
        updateStepStatuses(
          ['completed', 'completed', 'completed', 'active'],
          'processStreamEvent:开始执行过程',
          event
        );
      } else if (isImplicitStepFourStart) {
        pendingStepFourActivationRef.current = false;
        updateStepStatuses(
          ['completed', 'completed', 'completed', 'active'],
          'processStreamEvent:implicit-step4-runtime',
          event
        );
      } else if (isStepFourCompleteLog(event)) {
        pendingStepFourActivationRef.current = false;
        updateStepStatuses(
          ['completed', 'completed', 'completed', 'completed'],
          'processStreamEvent:完成执行过程',
          event
        );
      } else if (type === 'complete' && content === '执行完成') {
        pendingStepFourActivationRef.current = false;
        // 全部完成：步骤4打勾
        updateStepStatuses(
          ['completed', 'completed', 'completed', 'completed'],
          'processStreamEvent:执行完成',
          event
        );
        waitingDoneViewportRef.current = !readonlyReplayRef.current;
      }

      // 收到 error 类型则提示错误
      if (type === 'error') {
        console.error('[运行场景任务] 服务端返回错误:', content);
      }

      // 记录本次任务全局 requestId，供后续执行过程/结果查询复用。
      if (type === 'requestId') {
        currentRequestIdRef.current = content || undefined;
        setScenarioTaskRequestId(content || undefined);
      }

      // 数据集暂停：暂停页面队列并弹窗重选知识库，确认后调用 resume 接口继续。
      if (type === 'pause' && event.category === 'dataset') {
        console.info('[运行场景任务] 收到知识库暂停事件', {
          requestId: currentRequestIdRef.current,
          processName: event.processName || '',
          content,
        });
        pausePageQueue();
        setDatasetPause({
          content,
          processName: event.processName || '',
        });
      }

      // 提取 answer 内容
      if (type === 'answer' && !readonlyReplayRef.current) {
        answerContentRef.current = content;
        const answerPayload = createAssistantAnswerPayload(content);
        const messageIndex = currentAnswerMessageIndexRef.current;
        if (messageIndex !== null) {
          setMessages(prev => {
            if (messageIndex < 0 || messageIndex >= prev.length) return prev;
            const target = prev[messageIndex];
            if (!target || target.role !== 'assistant') return prev;
            const next = [...prev];
            next[messageIndex] = {
              ...target,
              content: answerPayload.content,
              displayMode: answerPayload.displayMode,
              structuredContent: answerPayload.structuredContent,
            };
            return next;
          });
        }
      }

      // requestId 仅作为内部上下文使用，不展示到右侧运行日志面板。
      if (type === 'requestId') {
        return;
      }

      // 所有流式类型都进入日志面板和图形组件；不同 type 再由下游解释成不同动作。
      pendingLogsRef.current.push({
        ...event,
        // 记录前端实际收到该条日志的时间，供右侧日志面板展示。
        timestamp:
          event.timestamp ||
          new Date().toLocaleString('zh-CN', { hour12: false }),
      });
      if (flushLogsTimerRef.current === null) {
        flushLogsTimerRef.current = window.setTimeout(flushPendingLogs, 80);
      }
    },
    [
      flushPendingLogs,
      logStepDebug,
      pausePageQueue,
      setScenarioTaskRequestId,
      updateStepStatuses,
    ]
  );

  /** 过程图最终视口动画结束后，才开始进入结果页前的等待倒计时。 */
  const handleDoneViewportSettled = useCallback(() => {
    if (!waitingDoneViewportRef.current) return;
    finishRunAfterViewportSettled();
  }, [finishRunAfterViewportSettled]);

  const appendAssistantErrorMessage = useCallback((errorMessage: string) => {
    const normalizedMessage = (errorMessage || '运行场景任务失败').trim();
    const nextMessage = createErrorAssistantMessage(
      normalizedMessage,
      new Date().toLocaleString('zh-CN')
    );
    currentAnswerMessageIndexRef.current = null;
    setMessages(prev => {
      const exists = prev.some(
        item =>
          item.role === 'assistant' && item.content === nextMessage.content
      );
      if (exists) return prev;
      return [...prev, nextMessage];
    });
  }, []);

  const settleTaskFailure = useCallback(
    (
      errorMessage: string,
      options: { keepCurrentPhase?: boolean; showToast?: boolean } = {}
    ) => {
      const normalizedMessage = (errorMessage || '运行场景任务失败').trim();
      pendingStepFourActivationRef.current = false;
      waitingDoneViewportRef.current = false;
      setDatasetPause(null);
      appendAssistantErrorMessage(normalizedMessage);
      setCanViewLastProcess(
        !!currentRequestIdRef.current ||
          !!runConfig?.requestId ||
          !!runConfig?.processJson
      );
      setIsRunning(false);
      setPhase(prev =>
        options.keepCurrentPhase && prev === 'processView' ? prev : 'result'
      );
      if (options.showToast !== false) {
        messageApi.error(normalizedMessage);
      }
    },
    [
      appendAssistantErrorMessage,
      messageApi,
      runConfig?.processJson,
      runConfig?.requestId,
    ]
  );

  /** API 队列出队后的页面入口：先进入页面队列，再由页面自主决定何时真正处理。 */
  const enqueuePageEvent = useCallback(
    (event: LogEntry) => {
      if (isStepFourStartLog(event)) {
        const shouldAutoSkip =
          !stepFlowRef.current.hasRecruitingStarted &&
          !stepFlowRef.current.hasRecruitingCompleted &&
          !stepFlowRef.current.hasMatchingCompleted &&
          !stepFlowRef.current.hasAutoSkipQueued;

        logStepDebug('enqueuePageEvent:step4-start-check', event, {
          shouldAutoSkip,
        });

        if (shouldAutoSkip) {
          stepFlowRef.current.hasAutoSkipQueued = true;
          enqueueToPageQueue({
            type: 'internal',
            category: 'page-step',
            name: '',
            content: 'auto-complete-recruiting',
          });
          enqueueToPageQueue({
            type: 'internal',
            category: 'page-step',
            name: '',
            content: 'auto-complete-matching',
          });
        }
      }

      // 页面层队列始终存在：即便 demo 未开启，也能提供暂停 / 继续能力。
      if (!enqueueToPageQueue(event)) {
        logStepDebug('enqueuePageEvent:process-immediately', event);
        processStreamEvent(event);
      }
    },
    [enqueueToPageQueue, logStepDebug, processStreamEvent]
  );

  const initializeRunSession = useCallback(() => {
    readonlyReplayRef.current = false;
    setIsHistoryReplaying(false);
    setCanViewLastProcess(false);
    setDatasetPause(null);
    waitingDoneViewportRef.current = false;
    updateStepStatuses(
      ['pending', 'pending', 'pending', 'pending'],
      'initializeRunSession:reset',
      null,
      undefined,
      true
    );
    pendingLogsRef.current = [];
    if (flushLogsTimerRef.current) {
      clearTimeout(flushLogsTimerRef.current);
      flushLogsTimerRef.current = null;
    }
    pendingStepFourActivationRef.current = false;
    waitingDoneViewportRef.current = false;
    setLogEntries([]);
    answerContentRef.current = '';
    currentAnswerMessageIndexRef.current = null;
    resetPageQueue();
    stepFlowRef.current = createInitialStepFlowState();

    createPageQueue({
      onItem: processStreamEvent,
      minIntervalMs: pageConsumeIntervalMs,
      getDelayMs: event => {
        if (isInternalPageEvent(event)) {
          return autoSkipStepHoldMs;
        }
        const { type, content } = event;
        if (type !== 'log' || pageStepHoldMs <= 0) return 0;
        if (
          content === '开始招募' ||
          content === '完成招募' ||
          content === '完成全部组队'
        ) {
          return pageStepHoldMs;
        }
        return 0;
      },
      getPostDelayMs: event => {
        if (event.type === 'log' && event.content === '完成全部组队') {
          return STEP4_POST_DELAY_MS;
        }
        return 0;
      },
      onDelayStart: applyStepTransitionPreview,
    });

    streamQueueRef.current?.destroy();
    streamQueueRef.current = new StreamQueue<LogEntry>(event => {
      if (event.history === '1') {
        logStepDebug('streamQueue:history-event', event, {
          isStepFourStart: isStepFourStartLog(event),
        });

        if (isStepFourStartLog(event)) {
          const shouldAutoSkip =
            !stepFlowRef.current.hasRecruitingStarted &&
            !stepFlowRef.current.hasRecruitingCompleted &&
            !stepFlowRef.current.hasMatchingCompleted &&
            !stepFlowRef.current.hasAutoSkipQueued;

          logStepDebug('streamQueue:history-step4-start-check', event, {
            shouldAutoSkip,
          });

          if (shouldAutoSkip) {
            stepFlowRef.current.hasAutoSkipQueued = true;
            processStreamEvent({
              type: 'internal',
              category: 'page-step',
              name: '',
              content: 'auto-complete-recruiting',
            });
            processStreamEvent({
              type: 'internal',
              category: 'page-step',
              name: '',
              content: 'auto-complete-matching',
            });
          }
        }
        processStreamEvent(event);
        return;
      }

      enqueuePageEvent(event);
    });
  }, [
    applyStepTransitionPreview,
    autoSkipStepHoldMs,
    createPageQueue,
    enqueuePageEvent,
    logStepDebug,
    pageConsumeIntervalMs,
    pageStepHoldMs,
    processStreamEvent,
    resetPageQueue,
    updateStepStatuses,
  ]);

  const requestScenarioTaskRequestId = useCallback(
    async (question: string, config: RunConfig) => {
      const payload: Record<string, unknown> = {
        组织ID: config.orgId,
        组织名称: config.orgName,
        场景本体ID: config.scenarioId,
        场景本体名称: config.scenarioName,
        用户问题: normalizeQuestionParameter(question),
      };

      if (config.objectOntologyId !== undefined) {
        payload['对象本体ID'] = config.objectOntologyId;
      }
      if (config.objectOntologyName) {
        payload['对象本体名称'] = config.objectOntologyName;
      }

      if (config.knowledgeBaseId) {
        payload['知识库ID'] = config.knowledgeBaseId;
      }
      if (config.knowledgeBaseName) {
        payload['知识库名称'] = config.knowledgeBaseName;
      }

      for (const item of config.variables) {
        const key = item.key.trim();
        if (!key) continue;

        if (item.type === 'number') {
          if (typeof item.value === 'number' && Number.isFinite(item.value)) {
            payload[key] = item.value;
          }
          continue;
        }

        payload[key] = typeof item.value === 'string' ? item.value : '';
      }

      const controller = new AbortController();
      let requestId = '';

      try {
        const body = await createScenarioTaskOrchestratorStream(
          payload as {
            组织ID: string;
            场景本体ID: number;
            对象本体ID?: number;
            用户问题: string;
            [key: string]: unknown;
          },
          controller
        );
        const reader = body.getReader();
        const decoder = new TextDecoder();
        const cache = { current: '' };
        let done = false;

        while (!done && !requestId) {
          const { value, done: streamDone } = await reader.read();
          done = streamDone;

          if (!value) continue;

          const chunk = decoder.decode(value, { stream: true });
          const items = parseStreamChunk(chunk, cache);

          for (const item of items) {
            if (item.type === 'error') {
              throw new Error(item.content || '创建任务失败');
            }

            if (item.type === 'requestId' && item.content) {
              requestId = item.content;
              break;
            }
          }
        }

        if (!requestId) {
          throw new Error('创建任务失败，未获取到 requestId');
        }

        try {
          await reader.cancel();
        } catch {
          // noop
        }

        controller.abort();
        return requestId;
      } finally {
        controller.abort();
      }
    },
    []
  );

  /** 发起真实日志流请求并逐条处理事件 */
  const startLogStream = useCallback(
    async (requestId: string) => {
      abortControllerRef.current = new AbortController();
      let replayingHistory = false;

      try {
        const body = await getScenarioTaskProcessLogsStream(
          requestId,
          abortControllerRef.current
        );

        const reader = body.getReader();
        const decoder = new TextDecoder();
        const cache = { current: '' };
        let done = false;

        while (!done) {
          const { value, done: streamDone } = await reader.read();
          done = streamDone;

          if (value) {
            const chunk = decoder.decode(value, { stream: true });
            const items = parseStreamChunk(chunk, cache);

            for (const item of items) {
              // error 类型不走队列，立即处理
              if (item.type === 'error') {
                setIsHistoryReplaying(false);
                processStreamEvent(item);
                await streamQueueRef.current?.flush();
                await flushPageQueue();
                streamQueueRef.current?.destroy();
                settleTaskFailure(item.content || '运行场景任务失败', {
                  keepCurrentPhase: readonlyReplayRef.current,
                  showToast: !readonlyReplayRef.current,
                });
                return;
              }

              if (item.history === '1') {
                if (!replayingHistory) {
                  replayingHistory = true;
                  setIsHistoryReplaying(true);
                }
              } else if (replayingHistory) {
                replayingHistory = false;
                setIsHistoryReplaying(false);
              }

              // 通过队列控制输出速度（演示模式下按间隔输出）
              streamQueueRef.current?.enqueue(item);
            }
          }
        }

        // 流读取完毕，等待队列中所有消息输出完成
        await streamQueueRef.current?.flush();
        await flushPageQueue();
        setIsHistoryReplaying(false);
        if (readonlyReplayRef.current) {
          setIsRunning(false);
        }
      } catch (err) {
        setIsHistoryReplaying(false);
        if ((err as Error).name === 'AbortError') {
          console.log('[运行场景任务] 日志流已取消');
          waitingDoneViewportRef.current = false;
          return;
        }
        console.error('[运行场景任务] 日志流请求失败:', err);
        resetPageQueue();
        settleTaskFailure(
          (err as Error).message || '获取运行日志失败，请稍后重试',
          {
            keepCurrentPhase: readonlyReplayRef.current,
            showToast: true,
          }
        );
      } finally {
        abortControllerRef.current = null;
      }
    },
    [flushPageQueue, processStreamEvent, resetPageQueue, settleTaskFailure]
  );

  const openProcessView = useCallback(async () => {
    if (!runConfig) return;

    if (phase === 'result' && logEntries.length === 0 && runConfig.requestId) {
      initializeRunSession();
      readonlyReplayRef.current = true;
      setCanViewLastProcess(true);
      setPhase('processView');
      startLogStream(runConfig.requestId);
      return;
    }

    setPhase('processView');
  }, [
    initializeRunSession,
    logEntries.length,
    phase,
    runConfig,
    startLogStream,
  ]);

  /** 用户点击"开始执行"后的回调 */
  const handleStartRun = useCallback(
    async (question: string, config: RunConfig) => {
      setUserQuestion(question);
      setRunConfig({
        ...config,
        createdAt: new Date().toLocaleString('zh-CN', { hour12: false }),
      });
      setCanViewLastProcess(false);
      setDatasetPause(null);
      currentRequestIdRef.current = undefined;
      setScenarioTaskRequestId(undefined);
      waitingDoneViewportRef.current = false;

      setMessages([
        {
          role: 'user',
          content: question,
          time: new Date().toLocaleString('zh-CN'),
        },
      ]);

      initializeRunSession();
      setPhase('running');
      setIsRunning(true);

      try {
        const references =
          referenceData.organizations.length > 0 &&
          referenceData.ontologyTree.length > 0 &&
          referenceData.knowledgeBases.length > 0
            ? referenceData
            : await loadReferenceData();

        const requestId = await requestScenarioTaskRequestId(question, config);
        currentRequestIdRef.current = requestId;
        setScenarioTaskRequestId(requestId);

        const detail = await getScenarioTaskRecordDetailByRequestId(requestId);
        const nextRunConfig = buildRunConfigFromRecord(detail, references);

        setRunConfig(nextRunConfig);
        setUserQuestion(question);
        startLogStream(requestId);
      } catch (err) {
        resetPageQueue();
        pendingStepFourActivationRef.current = false;
        waitingDoneViewportRef.current = false;
        setPhase('config');
        setIsRunning(false);
        messageApi.error((err as Error).message || '创建任务失败，请稍后重试');
      }
    },
    [
      initializeRunSession,
      loadReferenceData,
      messageApi,
      referenceData,
      resetPageQueue,
      requestScenarioTaskRequestId,
      setScenarioTaskRequestId,
      startLogStream,
    ]
  );

  useEffect(() => {
    if (!detailId && !detailRequestId) return;

    let cancelled = false;

    const loadDetailPage = async () => {
      setPageLoading(true);
      abortControllerRef.current?.abort();
      streamQueueRef.current?.destroy();
      currentRequestIdRef.current = undefined;
      setScenarioTaskRequestId(undefined);
      setDatasetPause(null);
      setResumeSubmitting(false);

      try {
        const references =
          referenceData.organizations.length > 0 &&
          referenceData.ontologyTree.length > 0 &&
          referenceData.knowledgeBases.length > 0
            ? referenceData
            : await loadReferenceData();
        const detail = detailId
          ? await getScenarioTaskRecordDetail(detailId)
          : await getScenarioTaskRecordDetailByRequestId(detailRequestId!);

        if (cancelled) return;

        const nextRunConfig = buildRunConfigFromRecord(detail, references);
        const nextMessages = buildMessagesFromRecord(detail);

        initializeRunSession();
        setRunConfig(nextRunConfig);
        setMessages(nextMessages);
        setUserQuestion(
          nextMessages.find(item => item.role === 'user')?.content || ''
        );
        currentRequestIdRef.current = detail.requestId || undefined;
        setScenarioTaskRequestId(detail.requestId || undefined);
        setCanViewLastProcess(
          detail.status !== 'running' &&
            (!!detail.requestId || !!nextRunConfig.processJson)
        );

        if (detail.status === 'running') {
          setPhase('running');
          setIsRunning(true);
          if (detail.requestId) {
            startLogStream(detail.requestId);
          }
        } else {
          setPhase('result');
          setIsRunning(false);
        }
      } catch (err) {
        if (cancelled) return;
        messageApi.error((err as Error).message || '加载任务详情失败');
        resetToList();
      } finally {
        if (!cancelled) {
          setPageLoading(false);
        }
      }
    };

    loadDetailPage();

    return () => {
      cancelled = true;
    };
  }, [
    detailId,
    detailRequestId,
    initializeRunSession,
    loadReferenceData,
    messageApi,
    resetToList,
    setScenarioTaskRequestId,
    startLogStream,
  ]);

  /** pause 弹窗取消：异常场景下的逃生口，终止当前运行并回到配置页。 */
  const handleCancelPause = useCallback(() => {
    setDatasetPause(null);
    abortControllerRef.current?.abort();
    streamQueueRef.current?.destroy();
    resetPageQueue();
    pendingStepFourActivationRef.current = false;
    waitingDoneViewportRef.current = false;
    if (completeTimerRef.current !== null) {
      window.clearTimeout(completeTimerRef.current);
      completeTimerRef.current = null;
    }
    if (detailId || detailRequestId) {
      resetToList();
      return;
    }
    setPhase('config');
    setIsRunning(false);
  }, [detailId, detailRequestId, resetPageQueue, resetToList]);

  /** pause 弹窗确认：提交新知识库后自动恢复页面队列。 */
  const handleResumeWithKnowledgeBase = useCallback(
    async (selection: { id: string; name: string }) => {
      if (!runConfig?.orgId) {
        messageApi.error('缺少组织ID，无法继续流程');
        return;
      }
      const requestId = currentRequestIdRef.current;
      if (!requestId) {
        messageApi.error('缺少任务执行ID，无法继续流程');
        return;
      }

      setResumeSubmitting(true);
      try {
        console.info('[运行场景任务] 提交知识库后继续执行', {
          requestId,
          knowledgeBaseId: selection.id,
          knowledgeBaseName: selection.name,
        });
        await resumeScenarioTaskProcess(
          runConfig.orgId,
          requestId,
          selection.id
        );
        setRunConfig(prev =>
          prev
            ? {
                ...prev,
                knowledgeBaseId: selection.id,
                knowledgeBaseName: selection.name,
              }
            : prev
        );
        setDatasetPause(null);
        resumePageQueue();
      } catch (err) {
        messageApi.error((err as Error).message || '继续流程失败，请稍后重试');
      } finally {
        setResumeSubmitting(false);
      }
    },
    [messageApi, resumePageQueue, runConfig?.orgId]
  );

  return (
    <div className="h-full min-h-0">
      {contextHolder}
      {pageLoading ? (
        <div className="flex h-full items-center justify-center">
          <Spin size="large" />
        </div>
      ) : phase === 'config' ? (
        <div className="h-full min-h-0">
          <ConfigAndSendQuestion
            messages={messages}
            onStartRun={handleStartRun}
            canViewProcess={canViewLastProcess}
            onViewProcess={openProcessView}
            onBackToList={resetToList}
          />
        </div>
      ) : phase === 'result' ? (
        <div className="h-full min-h-0">
          <ReadonlyResultView
            config={runConfig}
            messages={messages}
            onBackToList={resetToList}
            canViewProcess={canViewLastProcess}
            onViewProcess={openProcessView}
          />
        </div>
      ) : null}

      {runConfig && (
        <div
          className={
            phase === 'running' || phase === 'processView'
              ? 'h-full min-h-0'
              : 'hidden'
          }
        >
          <RunningProcess
            question={userQuestion}
            config={runConfig}
            stepStatuses={stepStatuses}
            logEntries={logEntries}
            isPaused={isPageQueuePaused}
            suppressDiagramMotion={isHistoryReplaying}
            showPauseControl={phase === 'running' && enablePauseControl}
            onTogglePause={togglePageQueue}
            onBackToList={resetToList}
            showViewResultLink={canViewLastProcess && phase !== 'running'}
            onViewResult={() => setPhase('result')}
            onDoneViewportSettled={handleDoneViewportSettled}
          />
        </div>
      )}
      <PauseKnowledgeBaseModal
        open={!!datasetPause}
        messageText={datasetPause?.content || ''}
        processName={datasetPause?.processName || ''}
        value={runConfig?.knowledgeBaseId}
        loading={resumeSubmitting}
        onConfirm={handleResumeWithKnowledgeBase}
        onCancel={handleCancelPause}
      />
    </div>
  );
};

export default ScenarioTaskPage;
